import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { DxSelectBoxModule, DxTemplateModule, DxTextBoxModule, DxValidatorModule } from 'devextreme-angular';
import { Subscription } from 'rxjs';
import { ContactRole, ContactService } from 'src/app/services/contact.service';
import { AppGlobals } from 'src/app/app.globals';
import { GpdButtonComponent } from '../gpd-button/gpd-button.component';
import { SvgIconComponent } from '../svg-icon/svg-icon.component';

// Shared "pick an existing role, add a new one, or fix a typo" control — replaces the
// acceptCustomValue/onCustomItemCreating pattern that used to be copy-pasted in
// project-add-contact-modal, project-new-contact-modal and contact-add-project-modal.
// Typing straight into a plain-looking dropdown to silently create a new role wasn't
// discoverable, so "add new role" is its own explicit affordance instead of a hidden
// side effect of typing into the select box — and each role in the list gets an edit
// pencil, since a typo committed via "add new role" used to be permanent (it lands in
// the shared ContactRoles master list, which has no delete/rename UI anywhere else in
// the app). The inline text row auto-commits on Enter or on blur (see finishEditing) —
// there's no separate confirm/cancel step: an explicit checkmark/cancel pair was tried
// first, but it was too easy to miss, so users would type a role, click the dialog's
// main submit button, and have it silently discarded because it was never "confirmed".
// Leaving the field empty and blurring away is the (implicit) way to back out.
@Component({
    selector: 'gpd-role-select',
    imports: [
        CommonModule,
        DxSelectBoxModule,
        DxTextBoxModule,
        DxValidatorModule,
        DxTemplateModule,
        GpdButtonComponent,
        SvgIconComponent,
    ],
    templateUrl: './gpd-role-select.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./gpd-role-select.component.css']
})
export class GpdRoleSelectComponent implements OnInit, OnDestroy {
  @Input() value: string = '';
  @Output() valueChange = new EventEmitter<string>();

  // Fired whenever a role (new or renamed) actually gets committed — via Enter or via
  // losing focus — so a parent step can advance/finish immediately if that was the last
  // thing it needed, instead of requiring a separate click on its own submit button.
  // Not fired when the row is just cancelled (empty text, no-op confirm).
  @Output() roleCommitted = new EventEmitter<void>();

  @Input() required: boolean = true;

  // Backed by ContactService's shared cache, not a local fetch — when several
  // gpd-role-select instances are open at once (one per row in a list), a role added or
  // renamed in one instance needs to show up in every other one immediately, not just on
  // next reload.
  roles: ContactRole[] = [];
  private rolesSub?: Subscription;

  // 'select': normal dropdown. 'add'/'edit' both reuse the same inline text-input row —
  // the only difference is what confirming it does.
  mode: 'select' | 'add' | 'edit' = 'select';
  inputText: string = '';
  private editingRole: ContactRole | null = null;
  private selectBoxInstance: any;

  constructor(private contactService: ContactService) {}

  ngOnInit(): void {
    this.rolesSub = this.contactService.roles$.subscribe((roles) => {
      if (roles !== null) {
        this.roles = roles;
      }
    });
    this.contactService.ensureRolesLoaded();
  }

  ngOnDestroy(): void {
    this.rolesSub?.unsubscribe();
  }

  onSelectBoxInitialized(e: any): void {
    this.selectBoxInstance = e.component;
  }

  onValueChanged(e: any): void {
    this.value = e.value;
    this.valueChange.emit(this.value);
  }

  startAddingNew(): void {
    this.mode = 'add';
    this.inputText = '';
  }

  // Called from the dropdown item template's pencil — role edits happen in the same
  // inline row as "add new", not inside the popup itself (DevExtreme's list items aren't
  // a reliable place to host an editable text field).
  startEditing(role: ContactRole, event: any): void {
    event?.stopPropagation();
    this.selectBoxInstance?.close();
    this.mode = 'edit';
    this.editingRole = role;
    this.inputText = role.name;
  }

  cancelEditing(): void {
    this.mode = 'select';
    this.inputText = '';
    this.editingRole = null;
  }

  // Called on blur (onFocusOut) of the inline text row — the field auto-commits instead
  // of requiring an explicit checkmark. Empty text is treated as "the user backed out"
  // and just reverts to the dropdown without creating/renaming anything.
  finishEditing(): void {
    const text = (this.inputText ?? '').trim();
    if (text) {
      this.commitAndNotify();
    } else {
      this.cancelEditing();
    }
  }

  // Bound to the text box's Enter key — same commit-and-notify as finishEditing, Enter
  // just skips the "is there text" branch since confirm() already no-ops on empty text.
  handleEnterKey(): void {
    this.commitAndNotify();
  }

  private async commitAndNotify(): Promise<void> {
    const committed = await this.confirm();
    if (committed) {
      this.roleCommitted.emit();
    }
  }

  async confirm(): Promise<boolean> {
    // Enter (handleEnterKey) and blur (onFocusOut → finishEditing) can both fire for the
    // same commit — Enter resolves mode back to 'select' first, so guard against a
    // second, now-stale confirm() call landing on top of it.
    if (this.mode === 'select') {
      return false;
    }

    const text = (this.inputText ?? '').trim();
    if (!text) {
      return false;
    }

    if (this.mode === 'add') {
      const existing = this.roles.find((r) => r.name.toLowerCase() === text.toLowerCase());
      const finalRole = existing?.name ?? text;
      if (!existing) {
        // Not persisted yet — the ContactRoles master row is only created server-side
        // once the parent form actually submits (addProjectContact/addPerson/etc.). id: 0
        // marks it as "not yet real": the edit pencil still shows for it, but confirm()
        // renames it locally instead of calling the backend (see the 'edit' branch below).
        // Goes through the shared cache so every other open gpd-role-select picks it up too.
        this.contactService.addLocalRole({ id: 0, name: text });
      }
      this.value = finalRole;
      this.valueChange.emit(finalRole);
    } else if (this.mode === 'edit' && this.editingRole) {
      const oldName = this.editingRole.name;
      const roleId = this.editingRole.id;

      // id 0 means this role only exists in the shared cache so far — it was just typed
      // via "add new role" and hasn't been submitted (so the parent form hasn't created its
      // ContactRoles row yet). Nothing to call the backend about; just rename it in place.
      if (roleId) {
        const result = await this.contactService
          .updateContactRole({ RoleId: roleId, NewName: text })
          .catch((error: any) => ({ hasError: true, feedback: String(error) }));

        if (result?.hasError) {
          AppGlobals.ShowErrorMessage(result.feedback);
          return false; // stay in edit mode so the mistake can be fixed and retried
        }
      }

      // Updates the shared cache, so every other open gpd-role-select reflects the rename
      // immediately too — not just the instance the edit was made in.
      this.contactService.renameRoleInCache(this.editingRole, text);

      // Keep the currently selected value in sync if it was the role just renamed.
      if (this.value?.toLowerCase() === oldName.toLowerCase()) {
        this.value = text;
        this.valueChange.emit(text);
      }
    }

    this.mode = 'select';
    this.inputText = '';
    this.editingRole = null;
    return true;
  }
}
